import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { PositionStaffingCommandServerPort } from '../commands/PositionStaffingCommand';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import type { PositionStaffingResult } from '../models/PositionStaffingIntent';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';
import { invalidateOrgPlanningQueries } from './invalidateOrgPlanningQueries';
import type { PositionStaffingOperationNotice } from './usePositionStaffingCommands';
import { recordPositionStaffingTelemetry } from '../services/PositionStaffingTelemetry';
import {
  PositionCommandSettlementError,
  isPositionCommandConflict,
  postPositionIntentWithReconciliation,
  settlePositionCommand,
} from './positionCommandSettlement';
import { usePositionStructureOptimisticStore } from '../stores/positionStructureOptimisticStore';
import { positionStructureIntentMatchesModel } from '../services/applyOptimisticPositionStructure';
import { liveOrgModelQueryKey } from './orgModelQueryKeys';
import { enqueueOrgPlanningTransition } from './orgPlanningCommandSequencer';
import { successionOptimisticContext } from '../stores/successionOptimisticSlateStore';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent, seedOrgPlanningReview } from './useOrgPlanningReviewScope';
import { createOrgPlanningDraftCommandPort, type DraftHistoryAction } from './createOrgPlanningDraftCommandPort';

export function usePositionStructureCommands(input: {
  folderId: string;
  diagramId?: string;
  onNotice?: (notice: PositionStaffingOperationNotice) => void;
  onScenarioChangeSet?: (changeSetId: string, scenarioId?: string | null) => void;
  /** Routes the intent through the canonical operational-chart confirmation PEP. */
  operational?: boolean;
}) {
  const { folderId, onNotice, operational = false } = input;
  const diagramId = input.diagramId ?? 'test';
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const currentModel = useCallback(
    () => queryClient.getQueryData<OrgModelProjection>(
      liveOrgModelQueryKey(folderId),
    ),
    [folderId, queryClient],
  );
  const currentVersion = useCallback(() => currentModel()?.graphVersion, [currentModel]);

  const submit = useCallback(async (intent: PositionStructureIntent, signal?: AbortSignal, publishStaged = true) => {
    const reviewScope = captureOrgPlanningReviewScope(folderId, diagramId);
    const optimistic = usePositionStructureOptimisticStore.getState();
    if (intent.mode === 'live' && !operational) optimistic.begin(folderId, intent);
    onNotice?.({ state: 'pending', message: operational ? 'Preparing organization change…' : intent.mode === 'scenario' ? 'Adding structure change to scenario…' : 'Saving position change…' });
    let response: Response;
    try {
      response = await postPositionIntentWithReconciliation(
        () => {
          if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
          return authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/${operational ? 'operational-structure-intents' : 'position-structure-intents'}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(intent), signal,
          });
        },
        () => onNotice?.({ state: 'pending', message: 'The request is still settling. Confirming the same structure intent…' }),
      );
    } catch (error) {
      if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw error;
      optimistic.clear(folderId, intent.intentId);
      const indeterminate = error instanceof PositionCommandSettlementError
        && error.code === 'request_timeout';
      if (indeterminate) await invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
      onNotice?.({
        state: indeterminate ? 'indeterminate' : 'failed',
        message: indeterminate
          ? 'The server outcome is not confirmed. Refresh the chart before making another position change.'
          : 'The position change did not settle. Check your connection before retrying.',
      });
      throw error;
    }
    const payload = await response.json().catch(() => ({})) as PositionStaffingResult & { error?: string };
    if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
    if (!response.ok) {
      optimistic.clear(folderId, intent.intentId);
      const settlementError = new PositionCommandSettlementError('http_error', response.status, payload.error);
      const conflict = isPositionCommandConflict(settlementError);
      if (conflict) await invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
      onNotice?.({ state: conflict ? 'conflict' : 'failed', message: structureError(payload.error) });
      recordPositionStaffingTelemetry({ action: telemetryStructureAction(intent.kind), mode: intent.mode, outcome: 'blocked' });
      throw new PositionCommandSettlementError('http_error', response.status, payload.error);
    }
    if (payload.intentId !== intent.intentId || !Number.isFinite(payload.graphVersion)
      || !['applied', 'staged', 'noop'].includes(payload.outcome)) {
      optimistic.clear(folderId, intent.intentId);
      onNotice?.({ state: 'failed', message: 'The server response was incomplete. Refresh the chart before retrying.' });
      throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
    }
    recordPositionStaffingTelemetry({ action: telemetryStructureAction(intent.kind), mode: intent.mode, outcome: payload.outcome });
    if (payload.outcome === 'staged') {
      optimistic.clear(folderId, intent.intentId);
      if (!publishStaged) return payload;
      if (operational && payload.changeSetId && payload.review) {
        seedOrgPlanningReview(queryClient, reviewScope, payload.changeSetId, payload.review);
      }
      if (payload.changeSetId) input.onScenarioChangeSet?.(payload.changeSetId, payload.scenarioId);
      onNotice?.({ state: 'idle', message: operational ? 'Operational change ready to review.' : 'Structure change added to scenario.' });
      void queryClient.invalidateQueries({ queryKey: ['atlas', 'organization-plans', folderId, diagramId], refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: ['atlas', 'organization-plan-review', folderId, diagramId], refetchType: 'none' });
    } else if (payload.outcome === 'noop') {
      optimistic.clear(folderId, intent.intentId);
    } else {
      optimistic.markCommitted(folderId, intent.intentId, payload.graphVersion);
      try {
        await settlePositionCommand(invalidateOrgPlanningQueries(queryClient, folderId), 'refresh_timeout');
      } catch {
        onNotice?.({
          state: 'indeterminate',
          message: 'Position change saved. The canonical chart is still catching up.',
        });
        return payload;
      }
      const refreshed = currentModel();
      if (
        refreshed
        && refreshed.graphVersion >= payload.graphVersion
        && positionStructureIntentMatchesModel(refreshed, intent)
      ) {
        optimistic.clear(folderId, intent.intentId);
      } else {
        onNotice?.({
          state: 'indeterminate',
          message: 'Position change saved. The canonical chart is still catching up.',
        });
        return payload;
      }
    }
    if (payload.outcome !== 'staged') onNotice?.({ state: 'idle', message: 'Position chart updated.' });
    return payload;
  }, [authenticatedFetch, currentModel, diagramId, folderId, input, onNotice, operational, queryClient]);

  const transition = useCallback(async (
    changeSetId: string,
    action: 'discard' | 'restore' | 'revert' | DraftHistoryAction,
    signal?: AbortSignal,
    publish = true,
    restoreChangeSetId?: string,
  ) => {
    onNotice?.({ state: 'pending', message: 'Updating position history…' });
    try {
      const response = await settlePositionCommand(
        authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${changeSetId}/${action}`, {
          method: 'POST', signal, ...(restoreChangeSetId ? {
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restoreChangeSetId }),
          } : {}),
        }),
        'request_timeout',
      );
      const transitioned = await response.json().catch(() => null) as { id?: string; error?: string } | null;
      if (!response.ok) throw new PositionCommandSettlementError('http_error', response.status, transitioned?.error);
      if (!transitioned?.id) throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
      if (publish) await settlePositionCommand(invalidateOrgPlanningQueries(queryClient, folderId), 'refresh_timeout');
      onNotice?.({ state: 'idle', message: 'Position history updated.' });
      return transitioned.id;
    } catch (error) {
      onNotice?.({
        state: error instanceof PositionCommandSettlementError && error.status === 409 ? 'conflict' : 'failed',
        message: error instanceof PositionCommandSettlementError && error.status === 409
          ? 'The chart changed. Review the latest structure and try again.'
          : 'The position history change did not settle. Refresh before retrying.',
      });
      throw error;
    }
  }, [authenticatedFetch, diagramId, folderId, onNotice, queryClient]);

  const createServerPort = useCallback((initial: PositionStructureIntent): PositionStaffingCommandServerPort => {
    if (initial.mode === 'scenario' && !operational) {
      return createOrgPlanningDraftCommandPort({
        folderId, diagramId, initial, queryClient,
        submit: (intent, signal) => submit(intent, signal, false),
        transition: (head, action, signal, restoreId) => transition(head, action, signal, false, restoreId),
        onNoop: () => onNotice?.({ state: 'idle', message: 'No structure change was needed.' }),
        onError: (error) => {
          const uncertain = error instanceof PositionCommandSettlementError
            && (error.code === 'request_timeout' || error.serverCode === 'malformed_response');
          onNotice?.({ state: uncertain ? 'indeterminate' : isPositionCommandConflict(error) ? 'conflict' : 'failed',
            message: uncertain ? 'The save outcome is not confirmed. Review the saved draft before retrying.'
              : isPositionCommandConflict(error) ? 'The selected plan or its head changed. Review it before retrying this edit.'
                : 'The draft change could not be saved.',
          });
        },
        onAccepted: (head, scenarioId) => {
          input.onScenarioChangeSet?.(head, scenarioId);
          onNotice?.({ state: 'idle', message: 'Structure change saved to draft.' });
        },
      });
    }
    const context = successionOptimisticContext(folderId, diagramId);
    let intent = initial;
    let result: PositionStaffingResult | undefined;
    let inverseChangeSetId: string | null = null;
    const adoptStagedHead = () => {
      if (result?.outcome !== 'staged' || !result.changeSetId) return;
      intent = {
        ...intent,
        scenarioChangeSetId: result.changeSetId,
        scenarioId: result.scenarioId ?? intent.scenarioId,
        expectedHeadChangeSetId: result.changeSetId,
      };
    };
    const serialized = (operation: (signal: AbortSignal) => Promise<void>) =>
      enqueueOrgPlanningTransition(context, operation, queryClient);
    return {
      execute: () => serialized(async (signal) => {
        result = await submit(intent, signal);
        adoptStagedHead();
      }),
      undo: () => serialized(async (signal) => {
        if (!result) return;
        if (!result.changeSetId || result.outcome === 'noop') return;
        if (result.outcome === 'staged') {
          throw new Error('Use Changes to return an operational change to editing.');
        }
        inverseChangeSetId = inverseChangeSetId ?? await transition(result.changeSetId, 'revert', signal);
      }),
      redo: () => serialized(async (signal) => {
        if (result?.outcome === 'applied' && inverseChangeSetId) {
          const restoredId = await transition(inverseChangeSetId, 'revert', signal);
          if (restoredId) result = { ...result, changeSetId: restoredId, graphVersion: currentVersion() ?? result.graphVersion };
          inverseChangeSetId = null;
          return;
        }
        intent = { ...intent, intentId: crypto.randomUUID(), expectedGraphVersion: currentVersion() ?? result?.graphVersion ?? intent.expectedGraphVersion, scenarioChangeSetId: intent.mode === 'scenario' ? intent.scenarioChangeSetId : undefined };
        result = await submit(intent, signal);
        adoptStagedHead();
      }),
    };
  }, [currentVersion, diagramId, folderId, input, onNotice, operational, queryClient, submit, transition]);

  return { createServerPort, submit };
}

function telemetryStructureAction(
  kind: PositionStructureIntent['kind'],
): Exclude<PositionStructureIntent['kind'], 'update_person'> | 'edit' {
  return kind === 'update_person' ? 'edit' : kind;
}

function structureError(code: string | undefined): string {
  if (code === 'projection_incomplete') return 'Position authoring is disabled because the organization view is incomplete.';
  if (code === 'review_required') return 'That change would create an invalid reporting structure or has unresolved dependencies.';
  if (code === 'scenario_revision_conflict') return 'A collaborator changed this scenario. Review the latest revision and try again.';
  if (code === 'graph_version_conflict') return 'The chart changed. Review the latest structure and try again.';
  return 'The position change could not be saved.';
}
